/**
 * TEMPORARY Sentry verification page.
 *
 * Visit /__sentry-check (unauthenticated) to trigger test events against the
 * self-hosted Sentry instance. Each button exercises a different capture path
 * so you can confirm the whole pipeline (init → tags → release → source maps)
 * is wired end-to-end.
 *
 * DELETE this file and its route entry in src/router.tsx once verification
 * is complete — it is not gated behind auth.
 */

import { useState, type CSSProperties } from 'react';
import * as Sentry from '@sentry/react';

export default function SentryCheck() {
  const [lastAction, setLastAction] = useState<string>('');

  const throwUncaught = () => {
    setLastAction('Uncaught exception');
    // Thrown inside a React event handler — caught by root ErrorBoundary.
    throw new Error('[sentry-check] uncaught render exception');
  };

  const captureDirect = () => {
    Sentry.captureException(new Error('[sentry-check] direct Sentry.captureException'));
    setLastAction('Direct captureException (no crash)');
  };

  const captureMessage = () => {
    Sentry.captureMessage('[sentry-check] Sentry.captureMessage info level', 'info');
    setLastAction('captureMessage (info)');
  };

  const triggerConsoleError = () => {
    console.error('[sentry-check] console.error forwarded via consoleLoggingIntegration');
    setLastAction('console.error');
  };

  const triggerAsyncRejection = async () => {
    setLastAction('Async rejection (via promise)');
    // eslint-disable-next-line @typescript-eslint/no-throw-literal
    await Promise.reject(new Error('[sentry-check] unhandled promise rejection'));
  };

  return (
    <div style={{ padding: 40, fontFamily: 'system-ui', maxWidth: 720, margin: '0 auto' }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8 }}>Sentry verification</h1>
      <p style={{ color: '#555', marginBottom: 24 }}>
        Each button triggers a different capture path. Watch the Issues feed at{' '}
        <code>sentry.skill-mine.com</code> — events should arrive within ~10 seconds.
      </p>

      <div style={{ display: 'grid', gap: 12 }}>
        <button onClick={throwUncaught} style={btn('#dc2626')}>1. Throw uncaught error (React boundary)</button>
        <button onClick={captureDirect} style={btn('#2563eb')}>2. Sentry.captureException directly</button>
        <button onClick={captureMessage} style={btn('#0891b2')}>3. Sentry.captureMessage</button>
        <button onClick={triggerConsoleError} style={btn('#d97706')}>4. console.error (consoleLoggingIntegration)</button>
        <button onClick={triggerAsyncRejection} style={btn('#7c3aed')}>5. Async promise rejection</button>
      </div>

      {lastAction && (
        <p style={{ marginTop: 20, color: '#059669' }}>
          ✓ Last action: <strong>{lastAction}</strong>
        </p>
      )}

      <hr style={{ margin: '32px 0' }} />
      <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>What to verify in Sentry UI</h2>
      <ul style={{ color: '#555', lineHeight: 1.7 }}>
        <li><code>release</code> should be your package.json version (not <code>unknown</code>)</li>
        <li><code>host</code> tag should match this page's hostname</li>
        <li><code>environment</code> should match VITE_SENTRY_ENVIRONMENT</li>
        <li>If logged in: <code>user_mode</code> tag (admin/learner) and <code>user.id</code> should be set</li>
        <li>Stack trace should point to <code>.tsx</code> lines (source maps working)</li>
      </ul>
    </div>
  );
}

const btn = (color: string): CSSProperties => ({
  padding: '12px 16px',
  background: color,
  color: 'white',
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 500,
  textAlign: 'left',
});
